import { Connection } from '@/types';
import { StandardizedConnection } from '@/types/connections';

export type ConnectionIdentity = {
  id?: string;
  connectorId?: string;
  connectionId?: string;
};

export function connectionCoversId(connection: ConnectionIdentity, id: string): boolean {
  return connection.id === id || connection.connectorId === id || connection.connectionId === id;
}

/** Owned ids that are not present on any fetched connector. */
export function findMissingOwnedConnectionIds(
  ownedIds: readonly string[],
  fetched: readonly ConnectionIdentity[],
): string[] {
  return ownedIds.filter((id) => !fetched.some((connection) => connectionCoversId(connection, id)));
}

export function listCompanyConnections(data: { connections?: unknown } | null | undefined): Connection[] {
  if (!data?.connections) return [];
  if (Array.isArray(data.connections)) return data.connections as Connection[];
  if (typeof data.connections === 'object') {
    return Object.values(data.connections as Record<string, Connection>);
  }
  return [];
}

export function toStandardizedConnection(conn: Connection | StandardizedConnection): StandardizedConnection {
  const status =
    conn.status === 'connected' || conn.status === 'pending' || conn.status === 'error'
      ? conn.status
      : 'pending';

  return {
    id: conn.id,
    name: conn.name,
    status,
    logoUrl: conn.logoUrl ?? '',
    service: conn.service || conn.type || 'unknown',
    connectorId: conn.connectorId || conn.id,
    description: conn.description || conn.name,
    connectionId: conn.connectionId || conn.id,
    type: conn.type || 'unknown',
    created_at: conn.created_at,
    metadata: conn.metadata,
    import_type: conn.import_type,
    ecommerce_subtype: conn.ecommerce_subtype,
    file_name: conn.file_name,
    csvImports: conn.csvImports,
  };
}

/**
 * Keep saved connections for owned ids the latest fetch omitted, so a
 * temporary API miss cannot drop them from the draft. When several saved
 * connections cover an id, the first one wins.
 */
export function preserveOwnedConnections(
  existing: Array<Connection | StandardizedConnection>,
  missingIds: readonly string[],
): StandardizedConnection[] {
  const preserved: StandardizedConnection[] = [];
  const covered = new Set<string>();

  for (const conn of existing) {
    const matchedIds = missingIds.filter((id) => !covered.has(id) && connectionCoversId(conn, id));
    if (matchedIds.length === 0) continue;
    preserved.push(toStandardizedConnection(conn));
    matchedIds.forEach((id) => covered.add(id));
  }

  for (const id of missingIds) {
    if (covered.has(id)) continue;
    preserved.push({
      id,
      name: 'Saved connection',
      status: 'pending',
      logoUrl: '',
      service: 'unknown',
      connectorId: id,
      description: 'Saved connection could not be refreshed',
      connectionId: id,
      type: 'unknown',
      created_at: '',
    });
  }

  return preserved;
}
